import { css } from '@emotion/react';
import { MDXProvider } from '@mdx-js/react';
import { Container, List, ListItem, Stack, styled, Typography } from '@mui/joy';
import { createFileRoute } from '@tanstack/react-router';

import { ContainerLayout } from '@/components/ContainerLayout';
import { MdxH1 } from '@/components/mdx/MdxH1';
import { MdxH2 } from '@/components/mdx/MdxH2';
import { MdxH3 } from '@/components/mdx/MdxH3';
import { MdxH4 } from '@/components/mdx/MdxH4';
import { MdxOl } from '@/components/mdx/MdxOl';
import { MdxP } from '@/components/mdx/MdxP';
import { MdxUl } from '@/components/mdx/MdxUl';
import { ListItemButtonLink } from '@/components/router/ListItemButtonLink';
import { FileRouteTypes } from '@/routeTree.gen';

const StyledList = styled(List)(
  ({ theme }) => css`
    border-right: 2px solid ${theme.palette.divider};
    flex-grow: 0;
    padding-top: ${theme.spacing(4)};
    padding-bottom: ${theme.spacing(4)};
    background-color: ${theme.vars.palette.background.level1};
  `,
);

const StyledListItemButtonLink = styled(ListItemButtonLink)(
  ({ theme }) => css`
    --variant-plainHoverBg: ${theme.vars.palette.background.level3};
    --variant-plainActiveBg: ${theme.vars.palette.background.level3};

    &.active {
      background-color: ${theme.vars.palette.background.level3};
    }
  `,
) as typeof ListItemButtonLink;

const HELP_PAGES: { to: FileRouteTypes['to']; title: string }[] = [
  { to: '/hilfe/funktionsuebersicht', title: 'Funktionsübersicht' },
  { to: '/hilfe/organisationsverwaltung', title: 'Organisationsverwaltung' },
  { to: '/hilfe/benutzerverwaltung', title: 'Benutzerverwaltung' },
  {
    to: '/hilfe/verkehrszeichen-datenmodell',
    title: 'Verkehrszeichen-Datenmodell',
  },
  { to: '/hilfe/verkehrszeichen-edit', title: 'Verkehrszeichenbearbeitung' },
  { to: '/hilfe/vorgaenge', title: 'Vorgänge (VRAO)' },
  { to: '/hilfe/datenschnittstellen', title: 'Datenschnittstellen' },
  { to: '/hilfe/release-notes', title: 'Release Notes' },
  { to: '/hilfe/klickanleitungen', title: 'Klickanleitungen' },
];

export const Route = createFileRoute('/_authenticated/hilfe')({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: HelpLayout,
});

function HelpLayout() {
  return (
    <Stack sx={{ backgroundColor: 'common.white', flex: 1 }} direction="row">
      <StyledList>
        <ListItem>
          <StyledListItemButtonLink to="/hilfe" activeOptions={{ exact: true }}>
            <Typography level="title-lg">Hilfe</Typography>
          </StyledListItemButtonLink>
        </ListItem>
        {HELP_PAGES.map(({ to, title }) => (
          <ListItem key={to}>
            <StyledListItemButtonLink to={to} activeOptions={{ exact: true }}>
              {title}
            </StyledListItemButtonLink>
          </ListItem>
        ))}
      </StyledList>
      <Container>
        <MDXProvider
          components={{
            h1: MdxH1,
            h2: MdxH2,
            h3: MdxH3,
            h4: MdxH4,
            p: MdxP,
            ul: MdxUl,
            ol: MdxOl,
          }}
        >
          <ContainerLayout />
        </MDXProvider>
      </Container>
    </Stack>
  );
}
